import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, Link, Stack, TextField, Typography } from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import api from '../../../../api/api';
import { normalizeAssetImages } from './AssetPhotos';

const options = { timeout: 180000, skipLoading: true };
const errorText = error => typeof error.response?.data?.detail === 'string'
  ? error.response.data.detail : 'Не удалось выполнить запрос. Попробуйте позже.';

export default function AssetAIEnrichment({ asset, onApply, disabled }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [details, setDetails] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [draft, setDraft] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState([]);
  const [fields, setFields] = useState({ description: true, search: true });
  const [busy, setBusy] = useState('');
  const busyRef = useRef(false);
  const imported = useRef({});
  const [error, setError] = useState('');
  const [photoMessage, setPhotoMessage] = useState('');
  const begin = () => {
    setName(asset.name || ''); setDetails(''); setSourceUrl(''); setDraft(null);
    setPhotos([]); setSelected([]); setError(''); setPhotoMessage(''); imported.current = {};
    setFields({ description: !asset.description?.trim(), search: ![
      ...(asset.search_metadata?.tags || []), ...(asset.search_metadata?.target_audience || []),
      ...(asset.search_metadata?.use_cases || [])].length });
    setOpen(true);
  };
  const search = async () => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy('Ищем товар и готовим описание…');
    setError(''); setDraft(null); setPhotos([]); setSelected([]); setPhotoMessage('');
    try {
      const { data } = await api.post('/assets/rental/ai/draft', {
        name: name.trim(), details: details.trim(), source_url: sourceUrl.trim(),
      }, options);
      setDraft(data);
      if (data.matched) {
        setBusy('Подбираем фотографии…');
        try {
          const result = await api.post('/assets/rental/ai/photos', { token: data.photos_token }, options);
          setPhotos(result.data.photos || []); setPhotoMessage(result.data.message || '');
        } catch (photoError) { setPhotoMessage(errorText(photoError)); }
      }
    } catch (requestError) { setError(errorText(requestError)); }
    finally { busyRef.current = false; setBusy(''); }
  };
  const apply = async () => {
    if (busyRef.current) return;
    const existing = normalizeAssetImages(asset.images);
    if (existing.length + selected.length > 8) {
      setError('Всего у актива может быть не более 8 фотографий. Выберите меньше фото.'); return;
    }
    busyRef.current = true; setBusy('Добавляем выбранные данные…'); setError('');
    try {
      const images = [];
      for (const token of selected) {
        if (!imported.current[token]) {
          const { data } = await api.post('/assets/rental/ai/import-image', { token }, options);
          imported.current[token] = data.url;
        }
        images.push(imported.current[token]);
      }
      const changes = {};
      if (fields.description) changes.description = draft.description;
      if (fields.search) changes.search_metadata = { ...asset.search_metadata, ...draft.search_metadata };
      if (images.length) changes.images = [...existing, ...images];
      onApply(changes); setOpen(false);
    } catch (importError) { setError(errorText(importError)); }
    finally { busyRef.current = false; setBusy(''); }
  };
  return <>
    <Button variant="outlined" startIcon={<AutoAwesomeOutlinedIcon />} disabled={disabled} onClick={begin}>Заполнить с ИИ</Button>
    <Dialog open={open} fullWidth maxWidth="md" onClose={() => { if (!busyRef.current) setOpen(false); }}>
      <DialogTitle>Заполнить карточку с ИИ</DialogTitle>
      <DialogContent dividers><Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">Укажи модель или ссылку на карточку товара. ИИ прочитает страницу и подготовит описание и поля для подбора ботом. Если страница недоступна, попробует найти товар в других источниках. Поиск в интернете по тесту стоит около 2–3 ₽; фактическая сумма может отличаться.</Typography>
        <TextField label="Название и модель товара" value={name} disabled={!!busy} inputProps={{ maxLength: 200 }} onChange={e => setName(e.target.value)} />
        <TextField label="Уточнение (необязательно)" multiline minRows={2} value={details} disabled={!!busy} inputProps={{ maxLength: 1500 }} onChange={e => setDetails(e.target.value)} />
        <TextField label="Ссылка на товар или сайт производителя (необязательно)" value={sourceUrl} disabled={!!busy} inputProps={{ maxLength: 2000 }} onChange={e => setSourceUrl(e.target.value)} />
        <Button variant="contained" disabled={!!busy || !name.trim()} onClick={search}>{draft ? 'Найти заново' : 'Найти и заполнить'}</Button>
        {busy && <Stack direction="row" spacing={1} alignItems="center" role="status"><CircularProgress size={20} /><Typography>{busy}</Typography></Stack>}
        {error && <Alert severity="error">{error}</Alert>}
        {draft && <>
          {draft.source_warning && <Alert severity="warning">{draft.source_warning}</Alert>}
          {Number.isFinite(draft.cost_rub) && <Typography variant="caption">Стоимость ответа: {draft.cost_rub.toFixed(2)} ₽</Typography>}
          {!draft.matched && <Alert severity="info">Нужно уточнить товар перед заполнением.</Alert>}
          {(draft.questions || []).map((q, index) => <Typography key={index}>{q}</Typography>)}
          {draft.matched && <>
            <Alert severity="info">Проверь результат. Выбранные поля заменят соответствующие данные в форме. Затем сохрани карточку обычной кнопкой.</Alert>
            <FormControlLabel control={<Checkbox checked={fields.description} disabled={!!busy} onChange={e => setFields({ ...fields, description: e.target.checked })} />} label="Применить описание" />
            <TextField label="Описание" multiline minRows={3} value={draft.description} disabled={!!busy} inputProps={{ maxLength: 1500 }} onChange={e => setDraft({ ...draft, description: e.target.value })} />
            <FormControlLabel control={<Checkbox checked={fields.search} disabled={!!busy} onChange={e => setFields({ ...fields, search: e.target.checked })} />} label="Применить поисковые поля" />
            {[['tags', 'Теги'], ['target_audience', 'Целевая аудитория'], ['use_cases', 'Сценарии использования']].map(([key, label]) =>
              <Box key={key}><Typography fontWeight={600}>{label}</Typography><Typography>{draft.search_metadata[key].join(' · ') || 'Нет данных'}</Typography></Box>)}
            <Typography variant="subtitle2">Источники</Typography>
            {draft.sources.map(source => <Link key={source.url} href={source.url} target="_blank" rel="noopener noreferrer">{source.title}</Link>)}
            <Typography variant="h6">Фотографии</Typography>
            <Typography variant="body2">Выбери фото именно своей модели, которые можно использовать в каталоге. Они добавятся к существующим.</Typography>
            {photoMessage && <Alert severity="info">{photoMessage}</Alert>}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2 }}>
              {photos.map((photo, index) => <Box key={photo.token} sx={{ border: '1px solid', borderColor: 'divider', p: 1, borderRadius: 1 }}>
                <Box component="img" src={photo.preview} alt={`Найденное фото ${index + 1}`} sx={{ width: '100%', height: 150, objectFit: 'contain' }} />
                <FormControlLabel label={`Фото ${index + 1}`} control={<Checkbox disabled={!!busy} checked={selected.includes(photo.token)} onChange={e => setSelected(prev => e.target.checked ? [...prev, photo.token] : prev.filter(t => t !== photo.token))} />} />
                <Link href={photo.source_url} target="_blank" rel="noopener noreferrer" variant="caption">Источник фото</Link>
              </Box>)}
            </Box>
          </>}
        </>}
      </Stack></DialogContent>
      <DialogActions>
        <Button disabled={!!busy} onClick={() => setOpen(false)}>Отмена</Button>
        <Button variant="contained" disabled={!!busy || !draft?.matched || (!fields.description && !fields.search && !selected.length)} onClick={apply}>Применить выбранное</Button>
      </DialogActions>
    </Dialog>
  </>;
}
